import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { errorMessageKey, unwrap } from '../../api/api-error';
import { ensureOk } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Notice } from '../../components/ui/notice';
import { useI18n } from '../../i18n/i18n';

const SESSIONS_KEY = ['me', 'sessions'] as const;

export function SessionsSection() {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const sessions = useQuery({
    queryKey: SESSIONS_KEY,
    queryFn: async () => unwrap(await api.GET('/api/v1/me/sessions')),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
  const revoke = useMutation({
    mutationFn: async (sessionId: string) =>
      ensureOk(
        await api.DELETE('/api/v1/me/sessions/{sessionId}', { params: { path: { sessionId } } }),
      ),
    onSuccess: refresh,
  });
  const revokeOthers = useMutation({
    mutationFn: async () => ensureOk(await api.POST('/api/v1/me/sessions/revoke-others')),
    onSuccess: refresh,
  });
  const formatDate = (iso: string) => new Date(iso).toLocaleString(locale);
  const error = revoke.error ?? revokeOthers.error ?? sessions.error;

  return (
    <Card title={t('profile.sessions.title')}>
      <ul className="divide-y divide-border text-sm">
        {sessions.data?.items.map((session) => (
          <li key={session.id} className="flex items-center justify-between gap-4 py-2">
            <div className="min-w-0">
              <p className="truncate">{session.userAgent ?? t('profile.sessions.unknownDevice')}</p>
              <p className="text-2xs text-muted-foreground">
                {session.ip ?? '—'} · {t('profile.sessions.lastSeen')}{' '}
                {formatDate(session.lastSeenAt)}
              </p>
            </div>
            {session.current ? (
              <span className="whitespace-nowrap text-2xs text-success">
                {t('profile.sessions.current')}
              </span>
            ) : (
              <Button type="button" variant="link" onClick={() => revoke.mutate(session.id)}>
                {t('profile.sessions.revoke')}
              </Button>
            )}
          </li>
        ))}
      </ul>
      {error !== null && <Notice tone="danger">{t(errorMessageKey(error))}</Notice>}
      <Button
        type="button"
        variant="secondary"
        onClick={() => revokeOthers.mutate()}
        disabled={revokeOthers.isPending}
      >
        {t('profile.sessions.revokeOthers')}
      </Button>
    </Card>
  );
}
